import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, ChevronRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import useReveal from '../hooks/useReveal';
import { IMAGES, CAPABILITIES, STATS, APPROACH } from '../mock';

const Reveal = ({ children, delay = 0, className = '' }) => {
  const [ref, inView] = useReveal();
  return (
    <div ref={ref} className={`${className} ${inView ? 'in-view' : ''} reveal`} style={{ animationDelay: `${delay}ms` }}>
      {children}
    </div>
  );
};

const Counter = ({ value, suffix }) => {
  const [ref, inView] = useReveal({ threshold: 0.4 });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const dur = 1600; const start = performance.now();
    const isFloat = !Number.isInteger(value);
    const tick = (t) => {
      const p = Math.min((t - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(isFloat ? +(value * eased).toFixed(1) : Math.round(value * eased));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [inView, value]);
  return <span ref={ref}>{n}{suffix}</span>;
};

const Home = () => {
  const heroRef = useRef(null);
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    const onScroll = () => setOffset(window.scrollY);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div id="top" className="bg-[#060608] text-white overflow-x-hidden">
      <Header />

      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-center pt-28 pb-16">
        <div className="absolute inset-0 overflow-hidden">
          <img src={IMAGES.heroBrain} alt="Neural data network"
               className="absolute right-0 top-0 h-full w-full md:w-3/5 object-cover object-center opacity-60"
               style={{ transform: `translateY(${offset * 0.12}px)` }} />
          <div className="absolute inset-0 bg-gradient-to-r from-[#060608] via-[#060608]/85 to-[#060608]/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#060608] via-transparent to-transparent" />
          <div className="absolute inset-0 grid-overlay opacity-40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 w-full">
          <div className="max-w-2xl pt-8">
            <h1 className="animate-fade-up font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.08] tracking-tight" style={{ animationDelay: '80ms' }}>
              We use our unique technology to measure the <span className="text-[#e11d2a] text-glow-red">metrics that matter</span>
            </h1>
            <p className="animate-fade-up mt-7 text-white/60 text-lg leading-relaxed max-w-xl" style={{ animationDelay: '160ms' }}>
              In the landscape of extinction, precision is next to godliness. We reveal how consumers truly experience mobile networks across the developing world.
            </p>
            <div className="animate-fade-up mt-10 flex flex-wrap items-center gap-4" style={{ animationDelay: '240ms' }}>
              <Link to="/contact" className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#e11d2a] hover:bg-[#c11624] transition-colors shadow-[0_0_30px_rgba(225,29,42,0.4)] text-sm tracking-wide">
                Start a Conversation <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <a href="#capabilities" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/15 hover:border-white/40 hover:bg-white/5 transition-colors text-sm tracking-wide">
                Our Capabilities <ChevronRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/30">
          <span className="text-[10px] tracking-[0.3em] uppercase">Scroll</span>
          <span className="w-px h-10 bg-gradient-to-b from-white/40 to-transparent" />
        </div>
      </section>

      {/* SPECIALTY */}
      <section className="relative py-28">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <Reveal>
            <span className="text-[11px] tracking-[0.3em] uppercase text-[#e11d2a]">Our Specialty</span>
            <h2 className="font-display font-light text-3xl md:text-4xl leading-snug mt-6 text-white/95">
              Our specialty is the international market — specifically the developing world, where our advanced analytics give local networks and commercial retailers the most complete and accurate information available.
            </h2>
            <p className="mt-8 text-white/50 text-lg max-w-2xl mx-auto">This is where we feel we can make the most difference for our clients.</p>
          </Reveal>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section id="capabilities" className="relative py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="max-w-2xl mb-16">
            <span className="text-[11px] tracking-[0.3em] uppercase text-[#e11d2a]">What We Do</span>
            <h2 className="font-display font-light text-3xl md:text-5xl mt-5">A holistic view of the consumer environment</h2>
            <p className="mt-5 text-white/50">Testing mobile network performance from both the network and the consumer point of view.</p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {CAPABILITIES.map((c, i) => (
              <Reveal key={c.title} delay={i * 90}>
                <div className="group h-full p-8 rounded-2xl bg-white/[0.03] border border-white/5 hover:border-[#e11d2a]/40 hover:bg-white/[0.05] transition-all duration-500 hover:-translate-y-1.5">
                  <div className="w-12 h-12 rounded-xl bg-[#e11d2a]/10 border border-[#e11d2a]/20 flex items-center justify-center mb-6 group-hover:bg-[#e11d2a]/20 transition-colors">
                    <c.icon className="w-6 h-6 text-[#e11d2a]" strokeWidth={1.6} />
                  </div>
                  <h3 className="font-display text-lg text-white/95 mb-3">{c.title}</h3>
                  <p className="text-white/50 text-sm leading-relaxed">{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* APPROACH */}
      <section id="approach" className="relative py-28 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <div className="relative">
              <img src={IMAGES.dataViz} alt="Data visualization" className="rounded-2xl w-full object-cover aspect-[4/3] border border-white/10" />
              <div className="absolute -inset-4 -z-10 bg-[#3884ff]/10 blur-3xl rounded-full animate-glow" />
              <div className="absolute -bottom-6 -right-6 hidden md:block p-5 rounded-xl bg-[#0d0d12] border border-white/10 shadow-2xl">
                <div className="flex items-center gap-3">
                  <Target className="w-8 h-8 text-[#e11d2a]" strokeWidth={1.4} />
                  <div>
                    <p className="font-display text-2xl">99.4%</p>
                    <p className="text-white/40 text-xs">Measurement accuracy</p>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
          <div>
            <Reveal>
              <span className="text-[11px] tracking-[0.3em] uppercase text-[#e11d2a]">Our Approach</span>
              <h2 className="font-display font-light text-3xl md:text-4xl mt-5 mb-4">From signal to insight</h2>
              <p className="text-white/50 mb-10">The objective is to provide the same deep insight into consumer behavior across the spectrum — from network operators to online retail operators.</p>
            </Reveal>
            <div className="space-y-6">
              {APPROACH.map((a, i) => (
                <Reveal key={a.step} delay={i * 100}>
                  <div className="flex gap-5 group">
                    <div className="font-display text-xl text-[#e11d2a]/70 group-hover:text-[#e11d2a] transition-colors w-10">{a.step}</div>
                    <div className="flex-1 pb-6 border-b border-white/5">
                      <h3 className="font-display text-xl text-white/95 mb-1">{a.title}</h3>
                      <p className="text-white/50 text-sm">{a.desc}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="relative py-20 border-t border-white/5">
        <div className="absolute inset-0 overflow-hidden">
          <img src={IMAGES.connections} alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-[#060608]/80" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 grid grid-cols-2 lg:grid-cols-4 gap-10">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 90} className="text-center">
              <p className="font-display font-light text-4xl md:text-5xl text-white text-glow-blue"><Counter value={s.value} suffix={s.suffix} /></p>
              <p className="mt-3 text-white/50 text-sm tracking-wide">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-28 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Reveal>
            <h2 className="font-display font-light text-3xl md:text-5xl leading-tight">Ready to measure what matters?</h2>
            <p className="mt-6 text-white/50 text-lg">Let’s talk about how precision analytics can bring clarity to your market.</p>
            <Link to="/contact" className="group inline-flex items-center gap-2 mt-10 px-8 py-4 rounded-full bg-[#e11d2a] hover:bg-[#c11624] transition-colors shadow-[0_0_30px_rgba(225,29,42,0.4)] text-sm tracking-wide">
              Contact Our Team <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
